import { useCallback, useContext, useState } from 'react'
import { AuthContext } from '../../../context'
import { useNavigate } from 'react-router-dom'
import AuthService from '../../../services/auth-service'
import useMounted from '../../../hooks/useMounted'

const useLogin = () => {
  const authContext = useContext(AuthContext)
  const isMounted = useMounted()

  const [wrongPassword, setWrongPassword] = useState(false)
  const [user, setUser] = useState({})
  const [rememberMe, setRememberMe] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const [inputs, setInputs] = useState({
    email: '',
    password: '',
  })

  const [errors, setErrors] = useState({
    emailError: false,
    passwordError: false,
  })

  const [loginError, setLoginError] = useState(false)
  const handleAddUser = useCallback(newUser => setUser(newUser), [])

  const handleSetRememberMe = useCallback(
    () => setRememberMe(!rememberMe),
    [rememberMe],
  )

  const handleChange = useCallback(
    e => {
      if (e.target.name === 'email' && errors.emailError) {
        setErrors(prev => ({
          ...prev,
          emailError: false,
        }))
      }

      if (loginError) {
        setLoginError(false)
      }
      setInputs({
        ...inputs,
        [e.target.name]: e.target.value,
      })
    },
    [errors.emailError, loginError, inputs],
  )

  const handleLoginWithData = useCallback(
    async data => {
      try {
        setIsLoading(true)
        const response = await AuthService.login(data)
        authContext.login(response.access_token, rememberMe)
      } catch (err) {
        setLoginError(true)
      } finally {
        setIsLoading(false)
      }
    },
    [authContext, rememberMe],
  )

  const handleSubmit = useCallback(
    async e => {
      // check remember me?
      e.preventDefault()

      const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

      if (
        inputs.email.trim().length === 0 ||
        !inputs.email.trim().match(mailFormat)
      ) {
        setErrors({ ...errors, emailError: true })
        return
      }

      if (inputs.password.trim().length < 6) {
        setErrors({ ...errors, passwordError: true })
        return
      }

      const newUser = { email: inputs.email, password: inputs.password }
      handleAddUser(newUser)

      const myData = {
        data: {
          type: 'token',
          attributes: { ...newUser },
        },
      }

      await handleLoginWithData(myData)
    },
    [inputs.email, inputs.password, handleAddUser, handleLoginWithData, errors],
  )

  const handleGoogleLoginSuccess = useCallback(
    async res => {
      const myData = {
        data: {
          type: 'google_token',
          token: res.credential,
        },
      }

      setWrongPassword(null)
      try {
        setIsLoading(true)
        const response = await AuthService.login(myData)
        setWrongPassword(false)
        if (response.hasOwnProperty('access_token')) {
          authContext.login(response.access_token, rememberMe)
        } else {
          navigate(
            `/resetare-parola?token=${response.register_token}&registering=1`,
          )
        }
      } catch (err) {
        // Should not get here
        setWrongPassword(false)
      } finally {
        setIsLoading(false)
      }
    },
    [authContext, navigate, rememberMe],
  )

  const handleGoogleLoginError = error => {
    console.log(error)
  }

  return {
    rememberMe,
    handleGoogleLoginSuccess,
    handleGoogleLoginError,
    errors,
    handleSetRememberMe,
    wrongPassword,
    handleSubmit,
    inputs,
    handleChange,
    isLoading,
    loginError,
  }
}

export default useLogin
